import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { farmAPI } from '@/lib/api';
import { resizeImageForUpload, extractBase64Payload } from '@/lib/imageResize';
import { useDiagnosisAvailable } from '@/lib/diagnosis';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import SubscriptionLock from '@/components/SubscriptionLock';
import ComingSoonPage from '@/components/ComingSoonPage';
import { hasFullAccess } from '@/lib/subscription';
import { extractErrorMessage } from '@/lib/errors';
import {
  Camera, Bug, Leaf, AlertTriangle, CheckCircle, Info, Loader2, X, Image as ImageIcon,
  ScanSearch, FlaskConical, Sprout,
} from 'lucide-react';

// Plantix answers in these of our languages; the others get English.
const PLANTIX_LANGUAGES = ['en', 'fr', 'ar'];
const LIKELIHOOD_STYLE = {
  very_likely: 'text-farm-error bg-farm-error/10',
  likely: 'text-farm-warning bg-farm-warning/10',
  possible: 'text-farm-info bg-farm-info/10',
};

function Bullets({ items, dot = 'bg-farm-primary' }) {
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="text-sm flex items-start gap-2">
          <span className={`w-1.5 h-1.5 ${dot} rounded-full mt-2 shrink-0`} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function Product({ product, t }) {
  const facts = [
    product.dose && t('diagnosis.products.dose', { dose: product.dose }),
    product.water && t('diagnosis.products.water', { water: product.water }),
    product.interval_days && t('diagnosis.products.interval', { days: product.interval_days }),
    product.max_applications && t('diagnosis.products.maxApplications', { count: product.max_applications }),
    product.pre_harvest_days && t('diagnosis.products.preHarvest', { days: product.pre_harvest_days }),
    product.reentry_hours && t('diagnosis.products.reentry', { hours: product.reentry_hours }),
  ].filter(Boolean);
  return (
    <div className="rounded-xl border p-3" data-testid="diagnosis-product">
      <p className="font-semibold">{product.name}</p>
      {product.active_ingredients?.length > 0 && (
        <p className="text-xs text-muted-foreground">{product.active_ingredients.join(', ')}</p>
      )}
      {facts.length > 0 && <div className="mt-2"><Bullets items={facts} /></div>}
      {product.warnings?.length > 0 && (
        <p className="mt-2 rounded-lg bg-amber-50 px-2 py-1.5 text-xs text-amber-900">{product.warnings.join(' ')}</p>
      )}
    </div>
  );
}

export default function DiagnosisPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const available = useDiagnosisAvailable();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [image, setImage] = useState(null);
  const [fromGallery, setFromGallery] = useState(false);
  const [preview, setPreview] = useState(null);
  const [crops, setCrops] = useState([]);
  const [crop, setCrop] = useState('');
  const [symptoms, setSymptoms] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // The farmer's own crops, so Plantix only considers diseases of that crop.
  useEffect(() => {
    if (!user?.id || !available) return;
    farmAPI.getPlots(user.id).then((res) => {
      const rows = Array.isArray(res.data) ? res.data : [];
      const names = rows
        .filter((p) => (p.produce_category || 'crops') === 'crops' && p.crop_type)
        .map((p) => p.crop_type);
      setCrops([...new Set(names)]);
    }).catch(() => setCrops([]));
  }, [user?.id, available]);

  const pick = (gallery) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: t('diagnosis.toast.imageTooLarge'), variant: 'destructive' });
      return;
    }
    setImage(file);
    setFromGallery(gallery);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
    setResult(null);
  };

  const handleDiagnose = async () => {
    if (!image) {
      toast({ title: t('diagnosis.toast.missingImage'), variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      // Resize before posting: 1280 px / q=0.8 keeps most leaf photos
      // under ~250 KB and well inside the server's upload limit.
      const resizedDataUrl = await resizeImageForUpload(image, { maxEdge: 1280, quality: 0.8 });
      const response = await farmAPI.createDiagnosis({
        user_id: user.id,
        image_base64: extractBase64Payload(resizedDataUrl),
        symptoms,
        crop_type: crop || 'Unknown',
        language: i18n.language,
        from_gallery: fromGallery,
      });
      setResult(response.data);
    } catch (error) {
      const status = error?.response?.status;
      toast({
        title: status === 413
          ? t('diagnosis.toast.imageTooBigForServer')
          : extractErrorMessage(error, t('diagnosis.toast.failed')),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const clearImage = () => {
    setImage(null);
    setPreview(null);
    setResult(null);
    [cameraRef, galleryRef].forEach((ref) => { if (ref.current) ref.current.value = ''; });
  };

  // Check subscription for farmers
  const hasSubscription = hasFullAccess(user);
  if (!hasSubscription) {
    return (
      <SubscriptionLock
        bodyKey="subscriptionLock.body.diagnosis"
        userType="farmer"
        benefitKeys={['aiDiagnosis', 'farmMgmt', 'weatherAlerts', 'expertConsult']}
      />
    );
  }
  if (available === null) {
    return (
      <div className="p-6 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-farm-primary" /></div>
    );
  }
  if (!available) {
    return (
      <ComingSoonPage titleKey="homeTiles.disease" bodyKey="diagnosis.comingSoonBody"
                      icon={ScanSearch} dataTestId="diagnosis-coming-soon" />
    );
  }

  const english = !PLANTIX_LANGUAGES.includes((i18n.language || 'en').split('-')[0]);
  const healthy = result?.crop_health === 'healthy';

  return (
    <div className="p-4 md:p-6 lg:p-8 animate-fadeIn">
      <div className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold">{t('diagnosis.title')}</h1>
        <p className="text-muted-foreground">{t('diagnosis.subtitle')}</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-farm-primary" />
              {t('diagnosis.upload.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <input type="file" ref={cameraRef} accept="image/*" capture="environment" onChange={pick(false)}
                   className="hidden" data-testid="diagnosis-camera-input" />
            <input type="file" ref={galleryRef} accept="image/*" onChange={pick(true)}
                   className="hidden" data-testid="diagnosis-file-input" />

            {preview ? (
              <div className="relative">
                <img src={preview} alt="" className="w-full h-64 object-cover rounded-xl" />
                <Button variant="destructive" size="icon" className="absolute top-2 right-2" onClick={clearImage}
                        aria-label={t('diagnosis.upload.remove')}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => cameraRef.current?.click()}
                        className="border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-farm-primary"
                        data-testid="diagnosis-take-photo">
                  <Camera className="w-8 h-8 text-farm-primary mx-auto mb-2" />
                  <span className="font-medium">{t('diagnosis.upload.takePhoto')}</span>
                </button>
                <button type="button" onClick={() => galleryRef.current?.click()}
                        className="border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-farm-primary"
                        data-testid="diagnosis-choose-photo">
                  <ImageIcon className="w-8 h-8 text-farm-primary mx-auto mb-2" />
                  <span className="font-medium">{t('diagnosis.upload.choosePhoto')}</span>
                </button>
                <p className="col-span-2 text-xs text-muted-foreground">{t('diagnosis.upload.tips')}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="diagnosis-crop">{t('diagnosis.cropLabel')}</Label>
              <select id="diagnosis-crop" value={crop} onChange={(e) => setCrop(e.target.value)}
                      className="w-full h-11 rounded-md border border-input bg-background px-3 text-sm"
                      data-testid="diagnosis-crop">
                <option value="">{t('diagnosis.cropNotSure')}</option>
                {crops.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </div>

            <div className="space-y-2">
              <Label>{t('diagnosis.symptomsLabel')}</Label>
              <Textarea placeholder={t('diagnosis.symptomsPlaceholder')} value={symptoms}
                        onChange={(e) => setSymptoms(e.target.value)} rows={3} data-testid="symptoms-input" />
            </div>

            <Button onClick={handleDiagnose} disabled={!image || loading}
                    className="w-full bg-farm-primary hover:bg-farm-primary-dark" data-testid="diagnose-btn">
              {loading
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('diagnosis.analyzing')}</>
                : <><Bug className="w-4 h-4 mr-2" /> {t('diagnosis.submit')}</>}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Leaf className="w-5 h-5 text-farm-primary" />
              {t('diagnosis.results.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!result && (
              <div className="text-center py-12">
                <Bug className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading text-lg font-medium mb-2">{t('diagnosis.results.empty.title')}</h3>
                <p className="text-sm text-muted-foreground">{t('diagnosis.results.empty.subtitle')}</p>
              </div>
            )}

            {result?.image_problem && (
              <div className="p-4 rounded-xl border border-amber-300 bg-amber-50 text-amber-900" data-testid="diagnosis-image-problem">
                <p className="font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{t('diagnosis.problem.title')}</p>
                <p className="text-sm mt-1">
                  {t(`diagnosis.problem.${result.image_problem}`, { defaultValue: t('diagnosis.problem.bad_image') })}
                </p>
                <Button variant="outline" className="mt-3" onClick={clearImage}>{t('diagnosis.problem.retry')}</Button>
              </div>
            )}

            {result && !result.image_problem && (
              <div className="space-y-5 animate-fadeIn" data-testid="diagnosis-result">
                {english && (
                  <p className="flex items-start gap-2 text-xs text-muted-foreground">
                    <Info className="w-4 h-4 shrink-0" />{t('diagnosis.results.inEnglish')}
                  </p>
                )}

                {healthy ? (
                  <div className="p-4 rounded-xl bg-farm-success/10 text-farm-success" data-testid="diagnosis-healthy">
                    <p className="font-heading font-semibold text-lg flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />{t('diagnosis.results.healthy')}
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-muted/50 rounded-xl">
                    <h3 className="font-heading font-semibold text-lg">{result.disease_name}</h3>
                    {result.scientific_name && (
                      <p className="text-xs italic text-muted-foreground">{result.scientific_name}</p>
                    )}
                    {result.likelihood && (
                      <span className={`inline-flex mt-2 px-2 py-1 rounded-full text-xs font-medium ${LIKELIHOOD_STYLE[result.likelihood] || 'text-muted-foreground bg-muted'}`}>
                        {t(`diagnosis.likelihood.${result.likelihood}`, { defaultValue: result.likelihood })}
                      </span>
                    )}
                  </div>
                )}

                {!healthy && result.description && (
                  <div>
                    <h4 className="font-medium mb-2">{t('diagnosis.results.description')}</h4>
                    <p className="text-sm text-muted-foreground">{result.description}</p>
                  </div>
                )}

                {!healthy && result.reference_photos?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">{t('diagnosis.results.compare')}</h4>
                    <div className="grid grid-cols-3 gap-2">
                      {result.reference_photos.map((src) => (
                        <img key={src} src={src} alt="" loading="lazy" className="w-full aspect-square object-cover rounded-lg" />
                      ))}
                    </div>
                  </div>
                )}

                {!healthy && result.cause && (
                  <div>
                    <h4 className="font-medium mb-2">{t('diagnosis.results.cause')}</h4>
                    <p className="text-sm text-muted-foreground">{result.cause}</p>
                  </div>
                )}

                {result.treatment?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-farm-success" />{t('diagnosis.results.treatment')}
                    </h4>
                    <div className="space-y-3">
                      {result.treatment.map((item) => (
                        <div key={item.kind}>
                          <p className="text-sm font-medium flex items-center gap-1.5">
                            {item.kind === 'organic' ? <Sprout className="w-4 h-4 text-farm-success" /> : <FlaskConical className="w-4 h-4 text-farm-warning" />}
                            {t(`diagnosis.results.${item.kind}`)}
                          </p>
                          <p className="text-sm text-muted-foreground">{item.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {result.products?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-1">{t('diagnosis.products.title')}</h4>
                    <p className="text-xs text-muted-foreground mb-2">{t('diagnosis.products.label')}</p>
                    <div className="space-y-2">{result.products.map((p) => <Product key={p.name} product={p} t={t} />)}</div>
                  </div>
                )}

                {result.prevention?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-farm-warning" />{t('diagnosis.results.prevention')}
                    </h4>
                    <Bullets items={result.prevention} dot="bg-farm-warning" />
                  </div>
                )}

                {!healthy && result.other_possibilities?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">{t('diagnosis.results.others')}</h4>
                    <Bullets items={result.other_possibilities.map((o) => `${o.name} (${t(`diagnosis.likelihood.${o.likelihood}`, { defaultValue: o.likelihood })})`)} />
                  </div>
                )}

                <p className="text-xs text-muted-foreground border-t pt-3">{t('diagnosis.results.source')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
